import React, { useState } from "react";
import { EvtSectionHeader } from "components/header";
import { useParams } from "react-router-dom";
import { PermissionControlledInput } from "permissions/components/input";
import { Button } from "ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "ui/components/dropdown-menu";
import { CustomList, Icon } from "components";
import { MENU } from "icons";
import { Avatar } from "ui/components/avatar";
import { RenderAvatar } from "components/render-avatar";
import FolderEmplopyeesInputSheet from "./input";
import { fetchFolderForDrive, removeFolderAccess } from "./api";
import { eventBus } from "utils/eventBus";
import { invalidateAllQueries } from "utils/utils";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

export function IntegrationDriveFolder() {
  const { project_slug, drive_id } = useParams();
  const [users, setUsers] = useState(null);

  const { data: folderData, isLoading } = useQuery({
    queryKey: ["drive-folder", drive_id],
    queryFn: () => fetchFolderForDrive({ id: drive_id }),
  });

  const folder = Array.isArray(folderData) ? folderData[0] : folderData;

  const fields = [
    "employee.id",
    "employee.first_name",
    "employee.last_name",
    "employee.avatar",
    "employee.grade",
    "employee.department.name",
    "employee.discord_user_id",
    "employee.email",
    "repository_role",
  ];
  const columns = [
    {
      id: "employee.id",
      header: "Name",
      accessorKey: "employee",
      cell: ({ getValue }) => {
        const user = getValue();
        if (!user) return null;
        return (
          <div className="flex items-center gap-2">
            <Avatar className="size-7">
              <RenderAvatar user={user} />
            </Avatar>
            <span>
              {user.first_name} {user.last_name}
            </span>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "",
      accessorKey: "employee.id",
      cell: ({ row }) => {
        const employeeId = row.original.employee?.id;
        return (
          <div className="flex items-center gap-2 justify-end opacity-0 group-hover:opacity-100 transition">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="outline">
                  <Icon icon={MENU} className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <PermissionControlledInput
                  rule="update:PC,JC"
                  collection="jobs"
                >
                  <DropdownMenuItem
                    onClick={async () => {
                      try {
                        const res = await removeFolderAccess({
                          folder_id: folder?.folder_id,
                          employee: employeeId,
                        });
                        toast.success(res?.message || "Successfully removed access");
                        invalidateAllQueries();
                        eventBus.emit("refresh:list", `drive/folder/list_access${drive_id}`);
                      } catch (error) {
                        const errorMessage =
                          error?.response?.errors?.[0]?.message ||
                          error?.message || "Failed to remove access";
                        toast.error(errorMessage);
                      }
                    }}
                  >
                    Remove Access
                  </DropdownMenuItem>
                </PermissionControlledInput>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      sortable: false,
      meta: { align: "right" },
    },
  ];

  const defaultFilters = {
    folder_id: folder?.folder_id,
  };

  if (isLoading) {
    return (
      <div className="flex-1">
        <EvtSectionHeader
          title="Loading folder ..."
          description="Fetching folder details"
        />
      </div>
    );
  }

  return (
    <div className="flex-1">
      <FolderEmplopyeesInputSheet employees={users} drive_id={drive_id} folder_id={folder?.folder_id} />
      <EvtSectionHeader
        title={"Google Drive"}
        description={project_slug || "No  provided"}
        className="py-6 border-b-1"
      />
      <CustomList
        storageKey={drive_id}
        endPoint="drive/folder/list_access"
        filters={defaultFilters}
        columns={columns}
        meta={{ fields }}
        perPage={25}
        sortBy={"employee.first_name"}
        hidePagination={true}
        showSearch={false}
        onDataChange={setUsers}
      />
    </div>
  );
}
